import type { AttivazioneDiDevice } from "../../api/devices";

/**
 * COME È COLLEGATA una cassaforte, derivato dalle sue attivazioni.
 *
 * ## Perché derivato e non una colonna su `devices`
 *
 * Il tipo di collegamento è un fatto dell'ATTIVAZIONE: descrive come quel pezzo
 * è stato messo in rete in quell'occasione, e cambia se lo si riattiva
 * diversamente. Copiarlo sul device creerebbe due verità che divergono al primo
 * cambio fatto da Attivazioni — e oggi nel codice non esiste NESSUNA
 * propagazione attivazione → device da cui agganciarsi: andrebbe scritta e poi
 * mantenuta su ogni creazione, modifica, cancellazione e riaggancio di
 * `device_id`. È lo stesso ragionamento già fatto per la catena
 * sopralluogo → installazione → device, che resta derivata.
 *
 * Per la stessa ragione sulla scheda del dispositivo è in SOLA LETTURA: si
 * modifica dov'è il dato, cioè sull'attivazione.
 *
 * ## Quale attivazione vince, quando ce n'è più d'una
 *
 * La più recente per `data_effettiva ?? data_attivazione` — la stessa cascata
 * con cui la sezione Attivazioni della scheda ordina le sue righe, così la card
 * e l'elenco non raccontano due storie diverse. Le attivazioni senza nessuna
 * delle due date vanno in fondo, non in testa.
 *
 * Le **Cessate** perdono contro qualunque attivazione ancora viva: una cessata
 * non dice come il pezzo è collegato adesso. Se però sono le uniche rimaste si
 * mostra lo stesso la più recente, dichiarandolo — l'ultimo collegamento noto
 * vale più di un trattino.
 *
 * Se due attivazioni vive dicono cose diverse lo si segnala invece di
 * sceglierne una in silenzio: su un device condiviso fra più clienti è un caso
 * possibile, e nasconderlo trasformerebbe un dato da verificare in un dato che
 * sembra certo.
 *
 * In un file suo e non dentro `DeviceDetail.tsx` perché è una regola con tre
 * casi particolari, e da qui si può esercitare senza montare un componente.
 */
export interface CollegamentoCorrente {
  /** 'LAN' | 'SIM' | 'Nessuna' — il valore così com'è sull'attivazione. */
  tipo: string;
  /** La data da cui vale, quando l'attivazione ne ha una. */
  data: string | null;
  /** Nessuna attivazione viva: quello mostrato è l'ultimo collegamento noto. */
  soloCessate: boolean;
  /** Due o più attivazioni vive che dichiarano collegamenti diversi. */
  discordi: boolean;
}

/** La data a cui un'attivazione si riferisce: quella vera, o la programmata. */
function quando(a: AttivazioneDiDevice): string | null {
  return a.data_effettiva ?? a.data_attivazione;
}

export function collegamentoCorrente(attivazioni: AttivazioneDiDevice[]): CollegamentoCorrente | null {
  // Le attivazioni che non dicono niente sul collegamento non partecipano: una
  // riga col campo vuoto non è una risposta "nessun collegamento", è un campo
  // che nessuno ha compilato. `Nessuna` invece È una risposta e resta.
  const conTipo = attivazioni.filter((a) => a.comunicazione_tipo);
  if (conTipo.length === 0) return null;

  const piuRecenti = (a: AttivazioneDiDevice, b: AttivazioneDiDevice) => {
    const da = quando(a);
    const db = quando(b);
    if (da && db) return da < db ? 1 : -1;
    return da ? -1 : db ? 1 : 0;
  };

  const vive = conTipo.filter((a) => a.stato !== "Cessata");
  const candidate = vive.length > 0 ? vive : conTipo;
  const corrente = [...candidate].sort(piuRecenti)[0];

  return {
    tipo: corrente.comunicazione_tipo as string,
    data: quando(corrente),
    soloCessate: vive.length === 0,
    discordi: new Set(vive.map((a) => a.comunicazione_tipo)).size > 1,
  };
}
